import { useEffect, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import type { List } from '@molt/shared';
import { Button, Dialog, EmptyState, IconButton } from '@/components';
import { cardDisplayTitle } from '@/lib/card-title';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface ArchiveRestoreDestinationDialogProps {
  readonly cardTitle: string;
  readonly lists: readonly List[];
  readonly open: boolean;
  readonly busy: boolean;
  readonly onRestore: (listId: string) => void;
  readonly onCancel: () => void;
}

export function ArchiveRestoreDestinationDialog({
  cardTitle,
  lists,
  open,
  busy,
  onRestore,
  onCancel,
}: ArchiveRestoreDestinationDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const [selectedListId, setSelectedListId] = useState<string>('');

  useEffect(() => {
    if (!open) setSelectedListId('');
  }, [open]);

  if (!open) return null;

  /* ⚠ THE LEAF, not an eighth author of `Untitled card`. */
  const cleanTitle = cardDisplayTitle(t, cardTitle);
  const selected = lists.some((list) => list.id === selectedListId) ? selectedListId : '';

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="sm"
      title={t('copyMove.restore.title')}
      description={t('copyMove.restore.description', { title: cleanTitle })}
      closeControl={
        <IconButton label={t('copyMove.restore.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={selected === ''}
            disabledReason={t('copyMove.restore.chooseList')}
            busy={busy}
            busyLabel={t('copyMove.restore.busy')}
            onClick={() => {
              if (selected !== '') onRestore(selected);
            }}
          >
            {t('copyMove.restore.submit')}
          </Button>
        </>
      }
    >
      {lists.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          headingLevel={3}
          title={t('copyMove.noOpenLists')}
          body={t('copyMove.restore.noListsBody')}
        />
      ) : (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">
            {t('copyMove.destinationListLabel')}
          </legend>
          <div className="flex flex-col gap-1">
            {lists.map((list) => (
              <label
                key={list.id}
                className="flex items-center gap-2 rounded-card px-2 py-1 text-sm text-ink hover:bg-surface-raised"
              >
                <input
                  type="radio"
                  name="archive-restore-destination"
                  value={list.id}
                  checked={selected === list.id}
                  onChange={() => setSelectedListId(list.id)}
                />
                <span className="min-w-0 truncate">{list.title}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </Dialog>
  );
}
